import { useCallback, useEffect, useMemo, useState } from 'react';
import type React from 'react';

import Form from '@rjsf/core';
import type {
  ErrorTransformer,
  RJSFValidationError,
  UiSchema
} from '@rjsf/utils';
import { customizeValidator } from '@rjsf/validator-ajv8';
import type { JSONSchema7 } from 'json-schema';

import { ArrayFieldTemplate } from '@/components/forms/components/formTemplates/arrayField';
import { FieldTemplate } from '@/components/forms/components/formTemplates/field';
import { ObjectFieldTemplateWriter } from '@/components/forms/components/formTemplates/objectFieldWriter';
import type { FormLinks } from '@/components/forms/utils/links';
import type { UploadFileFn } from '@/components/forms/utils/media';
import {
  type WorkflowContext,
  workflowGroupCustomFormat
} from '@/components/forms/utils/workflowContext';
import type { TraceContext } from '@/components/newLink/newLink.types';
import { CustomToast, notify } from '@/components/toast';
import { fields } from './fields';
import { validate } from './validate';
import { widgets } from './widgets';

type Props = {
  id: string;
  schema: JSONSchema7;
  uiSchema: UiSchema;
  disabled?: boolean;
  children?: React.ReactNode;
  submitLabel?: string;
  formData?: JSONSchema7;
  onChange: (data: unknown) => void;
  onSubmit: (value: Record<string, unknown>) => void;
  onError?: (errors: RJSFValidationError[]) => void;
  uploadFile: UploadFileFn;
  uploadConfig?: {
    maxFileSize?: number;
    fileExtensionWhitelistMap?: Record<string, string[]>;
  };
  links?: FormLinks;
  workflowContext?: WorkflowContext;
  isAnswer?: boolean;
  traceContext?: TraceContext;
};

type BlurHandlerFn = (id: string) => void;

export type FormWriterContext = {
  onErrorTriggered: boolean;
  blurredFields: string[];
  onBlur: BlurHandlerFn;
} & Pick<
  Props,
  | 'uploadFile'
  | 'uploadConfig'
  | 'links'
  | 'workflowContext'
  | 'isAnswer'
  | 'traceContext'
  | 'formData'
>;

export const FormWriter = ({
  id,
  schema,
  uiSchema,
  children = <div />,
  formData = {},
  onChange,
  onSubmit,
  onError,
  uploadFile,
  uploadConfig,
  links,
  workflowContext,
  traceContext
}: Props) => {
  const [onErrorTriggered, setOnErrorTriggered] = useState(false);
  const [blurredFields, setBlurredFields] = useState<string[]>([]);

  const resetBlurredFields = useCallback(() => {
    setBlurredFields([]);
  }, []);

  const scrollToError = useCallback(() => {
    const el = document.getElementById('field-error-message');
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, []);

  const handleError = useCallback(
    (errors: RJSFValidationError[]) => {
      // ? Log errors for debug purposes
      console.groupCollapsed('🚨 Form errors');
      errors.forEach(error => {
        console.error(error);
      });
      console.error('formData: ', formData);
      console.groupEnd();

      notify.error(
        <CustomToast
          multiDescription={errors.map(error => error.stack ?? '')}
          title="Form Error"
        />
      );

      onChange?.({ formData, errors });
      setOnErrorTriggered(true);
      // scroll to the first error
      scrollToError();

      // check if a callback has been provided in the props
      onError?.(errors);
    },
    [formData, onChange, scrollToError, onError]
  );

  const handleSubmit = useCallback(
    value => {
      onSubmit(value);
      resetBlurredFields();
    },
    [onSubmit, resetBlurredFields]
  );

  const transformErrors: ErrorTransformer = useCallback(errors => {
    // ! NOTE: Not sure if this is needed, we might want to remove it if this slows down the app
    return errors.map(error => {
      if (error.name === 'pattern') error.message = 'should match the format';
      if (
        error.name === 'required' &&
        error.params.missingProperty === error.property
      ) {
        error.message = 'is required';
      }
      return error;
    });
  }, []);

  const onBlurHandler: BlurHandlerFn = useCallback(id => {
    setBlurredFields(prevBlurredFields => {
      if (!prevBlurredFields.includes(id)) {
        return [...prevBlurredFields, id];
      }
      return prevBlurredFields;
    });
  }, []);

  useEffect(() => {
    if (onErrorTriggered) {
      scrollToError();
    }
  }, [onErrorTriggered, scrollToError]);

  const context: FormWriterContext = useMemo(
    () => ({
      uploadFile,
      uploadConfig,
      onErrorTriggered,
      blurredFields,
      onBlur: onBlurHandler,
      links,
      workflowContext,
      traceContext,
      formData
    }),
    [
      uploadFile,
      uploadConfig,
      onErrorTriggered,
      blurredFields,
      onBlurHandler,
      links,
      workflowContext,
      traceContext,
      formData
    ]
  );

  const validator = useMemo(
    () =>
      customizeValidator({
        customFormats: {
          'workflow-group': workflowGroupCustomFormat(workflowContext),
          draft: data => {
            try {
              JSON.parse(data);
              return true;
            } catch {
              return false;
            }
          }
        }
      }),
    [workflowContext]
  );

  return (
    <div className="w-full">
      <Form
        customValidate={validate(schema, uiSchema)}
        experimental_defaultFormStateBehavior={{
          arrayMinItems: {
            populate: 'never'
          }
        }}
        fields={fields}
        formContext={context}
        formData={formData || {}}
        id={id}
        idPrefix={id}
        liveValidate
        noHtml5Validate
        onChange={onChange}
        onError={handleError}
        onSubmit={handleSubmit}
        schema={schema}
        // !!! NOTE: This slows down the app A LOT, we need to optimize it or remove it
        showErrorList={false}
        templates={{
          FieldTemplate,
          ObjectFieldTemplate: ObjectFieldTemplateWriter,
          ArrayFieldTemplate
        }}
        // @ts-expect-error this should be removed since it's redundant with the formContext
        traceContext={traceContext}
        transformErrors={transformErrors}
        uiSchema={uiSchema}
        validator={validator}
        widgets={widgets}
      >
        {children}
      </Form>
    </div>
  );
};
